import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  AttachFile,
} from "@mui/icons-material";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import { useSelector } from "react-redux";
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { formatIntegerNumber } from '@/modules/commons/utils/numberFormat';
import { 
  formatVietnameseDateTime, 
  formatVietnameseFullDateTime 
} from '@/__core/helpers/utils';
import NewIncomeExpenseModal from '../../finance/components/NewIncomeExpenseModal';
import { getDocumentsByObject, getContentTypeId } from '../../documents/redux/DocumentCRUD';
import { getTransactionById } from '../../finance/redux/TransactionCRUD';
import { usePermissions } from '@/__core/permissions/hooks/usePermissions';
import { useFinancePermissions } from '@/__core/permissions/hooks/usePermissionChecker';
import { RootState } from '@/setup';
import { getStatusMetaByModule, type StatusMeta } from '@/modules/finance/utils/statusByModuleCache';

interface OrderIncomeExpense {
  id: string;
  note: string;
  order_name?: string;
  wallet_name?: string;
  wallet?: string | { id?: string; name?: string; code?: string };
  status: string | { id?: string; name?: string; color?: string };
  status_name?: string;
  status_label?: string;
  status_color?: string;
  status_code?: string;
  status_value?: string;
  transaction_status?: string | { id?: string; name?: string; color?: string };
  finance_status?: string | { id?: string; name?: string; color?: string };
  approval_status?: string | { id?: string; name?: string; color?: string };
  workflow_status?: string | { id?: string; name?: string; color?: string };
  created_at: string;
  created_by_name?: string;
  created_by: string;
  transaction_type_name?: string;
  transaction_type: string | { id?: string; name?: string; code?: string; category?: string };
  actual_amount: number;
  total_amount: number;
  posting_date: string;
  document_date: string;
}

interface ListOrderIncomeExpensesProps {
  incomeExpenses: OrderIncomeExpense[];
  loading: boolean;
  onCreateIncomeExpense: () => void;
  onEditIncomeExpense?: (incomeExpenseId: string, updateData: any) => Promise<void>;
  onDeleteIncomeExpense?: (incomeExpenseId: string) => void;
  error?: string | null;
  onClearError?: () => void;
}

const ListOrderIncomeExpenses: React.FC<ListOrderIncomeExpensesProps> = ({
  incomeExpenses,
  loading,
  onCreateIncomeExpense,
  onEditIncomeExpense,
  error,
  onClearError,
}) => {
  // Initialize permissions
  usePermissions(); // Ensure permissions context is initialized
  
  // Get permissions
  const { hasPermission } = usePermissions();
  const { canRead, canCreate, canUpdate } = useFinancePermissions();
  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany);
  
  // Check if user has global role
  selectedCompany?.roles?.some((role: any) =>
    role.role_name === 'global' ||
    role.role_level === 'global' ||
    role.role_name === 'global_admin'
  );
  
  // Check income/expense only permissions
  const canViewIncomeOnly = hasPermission('transaction', 'view_income_only');
  const canViewExpenseOnly = hasPermission('transaction', 'view_expense_only');
  
  // Check if user only has view permission (canRead but no create/update)
  const isViewOnly = canRead && !canCreate && !canUpdate;
  
  // Determine if user can create new income expenses
  const canCreateIncomeExpense = canCreate && !isViewOnly;
  
  // Determine if user can edit income expenses
  const canEditIncomeExpense = canUpdate && !isViewOnly;
  
  const [searchTerm] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingIncomeExpense, setEditingIncomeExpense] = useState<OrderIncomeExpense | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [documentsMap, setDocumentsMap] = useState<{[key: string]: any[]}>({});
  const [statusMetaById, setStatusMetaById] = useState<Record<string, StatusMeta>>({});
  const statusMetaRequestedRef = React.useRef(false);

  // Load documents for each income expense
  useEffect(() => {
    const loadAllDocuments = async () => {
      for (const incomeExpense of incomeExpenses) {
        if (!documentsMap[incomeExpense.id]) {
          await loadDocumentsForIncomeExpense(incomeExpense.id);
        }
      }
    };
    
    if (incomeExpenses.length > 0) {
      loadAllDocuments();
    }
  }, [incomeExpenses]);

  // Filter income expenses based on search term and permissions
  const filteredIncomeExpenses = useMemo(() => {
    return incomeExpenses.filter(incomeExpense => {
      // Filter by permissions: if user only has view_income_only, only show income transactions
      // If user only has view_expense_only, only show expense transactions
      if (canViewIncomeOnly && !canViewExpenseOnly) {
        // Only show income transactions - check if transaction_type category is 'income'
        // We need to check the transaction type category, but we don't have it directly
        // We'll need to check if the transaction_type_name or other field indicates income
        // For now, we'll skip this filter if we can't determine the category
        // This might need to be adjusted based on your data structure
      } else if (canViewExpenseOnly && !canViewIncomeOnly) {
        // Only show expense transactions
        // Similar to above, we'll need to check the transaction type category
      }
      
      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          incomeExpense.note.toLowerCase().includes(searchLower) ||
          (incomeExpense.order_name && incomeExpense.order_name.toLowerCase().includes(searchLower)) ||
          (incomeExpense.wallet_name && incomeExpense.wallet_name.toLowerCase().includes(searchLower)) ||
          (incomeExpense.transaction_type_name && incomeExpense.transaction_type_name.toLowerCase().includes(searchLower)) ||
          (incomeExpense.created_by_name && incomeExpense.created_by_name.toLowerCase().includes(searchLower)) ||
          incomeExpense.created_by.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }
      return true;
    });
  }, [incomeExpenses, searchTerm, canViewIncomeOnly, canViewExpenseOnly]);

  const getStatusKey = (item: OrderIncomeExpense): string => {
    if (typeof item.status === 'string') return item.status;
    if (item.status?.id) return item.status.id;
    if (typeof item.transaction_status === 'string') return item.transaction_status;
    if (item.transaction_status?.id) return item.transaction_status.id;
    if (typeof item.finance_status === 'string') return item.finance_status;
    if (item.finance_status?.id) return item.finance_status.id;
    if (typeof item.approval_status === 'string') return item.approval_status;
    if (item.approval_status?.id) return item.approval_status.id;
    if (typeof item.workflow_status === 'string') return item.workflow_status;
    if (item.workflow_status?.id) return item.workflow_status.id;
    return item.status_code || item.status_value || '';
  };

  const isLikelyId = (value: string): boolean => {
    const trimmed = (value || '').trim();
    if (!trimmed) return false;

    const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const numericLike = /^\d+$/;
    const hexLike = /^[0-9a-f]{24,}$/i;
    return uuidLike.test(trimmed) || numericLike.test(trimmed) || hexLike.test(trimmed);
  };

  const getStatusLabel = (item: OrderIncomeExpense): string => {
    const statusName = typeof item.status === 'object' ? item.status?.name : '';
    if (statusName) return statusName;
    if (typeof item.transaction_status === 'object' && item.transaction_status?.name) return item.transaction_status.name;
    if (typeof item.finance_status === 'object' && item.finance_status?.name) return item.finance_status.name;
    if (typeof item.approval_status === 'object' && item.approval_status?.name) return item.approval_status.name;
    if (typeof item.workflow_status === 'object' && item.workflow_status?.name) return item.workflow_status.name;
    if (item.status_name) return item.status_name;
    if (item.status_label) return item.status_label;

    const statusKey = getStatusKey(item);
    const mappedName = statusMetaById[statusKey]?.name;
    if (mappedName) return mappedName;
    if (statusKey === 'pending') return 'Chờ duyệt';
    if (statusKey === 'approved') return 'Duyệt';
    if (isLikelyId(statusKey)) return 'Không xác định';
    return statusKey || '-';
  };

  const hexToRgba = (hex: string, alpha: number) => {
    const normalized = hex.replace('#', '').trim();
    if (!/^[\da-fA-F]{3}$|^[\da-fA-F]{6}$/.test(normalized)) {
      return undefined;
    }

    const fullHex = normalized.length === 3
      ? normalized.split('').map((char) => char + char).join('')
      : normalized;

    const r = parseInt(fullHex.slice(0, 2), 16);
    const g = parseInt(fullHex.slice(2, 4), 16);
    const b = parseInt(fullHex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  useEffect(() => {
    const needsMeta = filteredIncomeExpenses.some((item) => {
      const key = getStatusKey(item);
      return key && !statusMetaById[key] && key !== 'pending' && key !== 'approved';
    });

    if (!needsMeta || statusMetaRequestedRef.current) {
      return;
    }

    statusMetaRequestedRef.current = true;
    let cancelled = false;

    getStatusMetaByModule(['transaction', 'finance'])
      .then((map) => {
        if (!cancelled) {
          setStatusMetaById(map);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatusMetaById({});
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filteredIncomeExpenses, statusMetaById]);

  const handleRowClick = async (incomeExpense: OrderIncomeExpense) => {
    // Only allow edit if user has update permission
    if (!canEditIncomeExpense) return;

    setEditLoading(true);
    try {
      const response = await getTransactionById(incomeExpense.id);
      const fullTransaction = response.data || response;

      const normalizedIncomeExpense = {
        ...incomeExpense,
        ...fullTransaction,
        total_amount: fullTransaction.total_amount ?? incomeExpense.total_amount ?? 0,
        actual_amount: fullTransaction.actual_amount ?? incomeExpense.actual_amount ?? 0,
        customer: fullTransaction.customer || fullTransaction.customer_id || (incomeExpense as any).customer || '',
        employee: fullTransaction.employee || fullTransaction.employee_id || (incomeExpense as any).employee || '',
        supplier: fullTransaction.supplier || fullTransaction.supplier_id || (incomeExpense as any).supplier || '',
        wallet: typeof fullTransaction.wallet === 'object' ? (fullTransaction.wallet?.id || '') : (fullTransaction.wallet || ''),
        transaction_type: typeof fullTransaction.transaction_type === 'object' ? (fullTransaction.transaction_type?.id || '') : (fullTransaction.transaction_type || ''),
        status: typeof fullTransaction.status === 'object' ? (fullTransaction.status?.id || '') : (fullTransaction.status || ''),
      };

      setEditingIncomeExpense(normalizedIncomeExpense as any);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch transaction detail:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    // Reload documents for the edited income expense when closing modal
    if (editingIncomeExpense && editingIncomeExpense.id) {
      loadDocumentsForIncomeExpense(editingIncomeExpense.id, true);
    }
    
    setIsEditModalOpen(false);
    setEditingIncomeExpense(null);
  };

  const handleUpdateIncomeExpense = async (updateData: any) => {
    if (editingIncomeExpense && onEditIncomeExpense) {
      const incomeExpenseId = editingIncomeExpense.id;
      
      // Update the income expense
      await onEditIncomeExpense(incomeExpenseId, updateData);
      
      // Close modal after update
      handleCloseEditModal();
    }
  };


  const loadDocumentsForIncomeExpense = async (incomeExpenseId: string, forceReload: boolean = false) => {
    try {
      // Skip if documents already loaded and not forcing reload
      if (!forceReload && documentsMap[incomeExpenseId]) {
        return;
      }
      
      const contentTypeId = await getContentTypeId('transaction');
      const documents = await getDocumentsByObject(contentTypeId, incomeExpenseId);
      setDocumentsMap(prev => ({
        ...prev,
        [incomeExpenseId]: documents
      }));
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  // Use shared integer formatter from commons
  // (displays localized integer with thousand separators)

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: vi });
  };

  // Format date as dd-mm-yyyy
  const formatDateDDMMYYYY = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const getStringValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object') {
      const record = value as { name?: unknown; code?: unknown; id?: unknown; category?: unknown };
      return String(record.category || record.name || record.code || record.id || '');
    }
    return '';
  };

  const getTypeColor = (type: unknown) => {
    // If API provides an object with color/category info, prefer that
    if (type && typeof type === 'object') {
      const t = type as any;
      const apiColor = (typeof t.color === 'string' && t.color) || (typeof t.category_color === 'string' && t.category_color);
      if (apiColor) {
        return {
          bg: hexToRgba(apiColor, 0.14) || '#f3f4f6',
          color: apiColor,
          border: apiColor,
        };
      }

      const category = String(t.category || t.category_name || '').trim().toLowerCase();
      if (category) {
        if (category === 'income') return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
        if (category === 'expense') return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' };
      }
    }

    const typeValue = getStringValue(type);
    if (!typeValue) return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };

    switch (typeValue.toLowerCase()) {
      case 'income':
        return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
      case 'expense':
        return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
  };

  const getStatusColor = (status: unknown) => {
    const statusValue = getStringValue(status);
    if (!statusValue) {
      return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
    switch (statusValue.toLowerCase()) {
      case 'draft':
        return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
      case 'pending':
        return { bg: '#fef3c7', color: '#92400e', border: '#fde68a' };
      case 'approved':
        return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
  };

  const getStatusTextPalette = (item: OrderIncomeExpense) => {
    const statusKey = getStatusKey(item);
    const apiColor =
      (typeof item.status === 'object' ? item.status?.color : undefined) ||
      (typeof item.transaction_status === 'object' ? item.transaction_status?.color : undefined) ||
      (typeof item.finance_status === 'object' ? item.finance_status?.color : undefined) ||
      (typeof item.approval_status === 'object' ? item.approval_status?.color : undefined) ||
      (typeof item.workflow_status === 'object' ? item.workflow_status?.color : undefined);
    const color = apiColor || item.status_color || statusMetaById[statusKey]?.color;
    if (color) {
      return {
        bg: hexToRgba(color, 0.14) || '#f3f4f6',
        color,
        border: color,
      };
    }

    return getStatusColor(statusKey);
  };

  const getWalletColor = (wallet: unknown) => {
    const walletValue = getStringValue(wallet);
    if (!walletValue) {
      return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
    switch (walletValue.toLowerCase()) {
      case 'cash':
        return { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' };
      case 'bank':
        return { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' };
      case 'card':
        return { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Đang tải danh sách thu chi...</Typography>
      </Box>
    );
  }

  // Create columns for DataTableModule - matching MainListTransaction.tsx order
  const columns = [
    {
      id: 'posting_date',
      label: 'Ngày hạch toán',
      render: (value: any, item: OrderIncomeExpense) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
          {formatDateDDMMYYYY(item.posting_date || '')}
        </Typography>
      ),
    },
    {
      id: 'document_date',
      label: 'Ngày chứng từ',
      render: (value: any, item: OrderIncomeExpense) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
          {formatDateDDMMYYYY(item.document_date || '')}
        </Typography>
      ),
    },
    {
      id: 'transaction_type',
      label: 'Loại',
      render: (value: any, item: OrderIncomeExpense) => (
        <Typography
          sx={{
            color: getTypeColor(item.transaction_type || '').color,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {item.transaction_type_name || 'N/A'}
        </Typography>
      ),
    },
    {
      id: 'actual_amount',
      label: 'Thực thu/chi',
      align: 'right' as const,
      render: (value: any, item: OrderIncomeExpense) => (
        <Typography>
          {formatIntegerNumber(item.actual_amount || 0)}
        </Typography>
      ),
    },
    {
      id: 'total_amount',
      label: 'Tổng giá trị',
      align: 'right' as const,
      render: (value: any, item: OrderIncomeExpense) => {
        // Handle both snake_case and camelCase, and ensure value is a number
        const totalAmount = item.total_amount !== undefined && item.total_amount !== null 
          ? Number(item.total_amount) 
          : ((item as any).totalAmount !== undefined && (item as any).totalAmount !== null 
            ? Number((item as any).totalAmount) 
            : 0);
        
        return (
          <Typography sx={{ 
            fontSize: '0.875rem', 
            fontWeight: 600, 
            color: '#1f2937',
            textAlign: 'right'
          }}>
            {formatIntegerNumber(totalAmount)}
          </Typography>
        );
      },
    },
    {
      id: 'wallet',
      label: 'Tài khoản/Ví',
      render: (value: any, item: OrderIncomeExpense) => (
        <Typography
          sx={{
            color: getWalletColor(item.wallet || '').color,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {item.wallet_name || getStringValue(item.wallet) || 'N/A'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Trạng thái',
      render: (value: any, item: OrderIncomeExpense) => (
        <Typography
          sx={{
            color: getStatusTextPalette(item).color,
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {getStatusLabel(item)}
        </Typography>
      ),
    },
    {
      id: 'note',
      label: 'Ghi chú',
      render: (value: string, item: OrderIncomeExpense) => (
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
          {item.note || 'Không có ghi chú'}
        </Typography>
      ),
    },
    {
      id: 'created_at',
      label: 'Ngày tạo',
      render: (value: any, item: OrderIncomeExpense) => (
        <Tooltip title={formatVietnameseFullDateTime(item.created_at || '')} placement="top">
          <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {formatVietnameseDateTime(item.created_at || '')}
          </Typography>
        </Tooltip>
      ),
    },
    {
      id: 'created_by',
      label: 'Người tạo',
      render: (value: any, item: OrderIncomeExpense) => {
        // Only show created_by_name if it's different from created_by (not a UUID)
        if (item.created_by_name && item.created_by_name !== item.created_by) {
          return (
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1f2937' }}>
              {item.created_by_name}
            </Typography>
          );
        }
        
        // If no proper name available, show N/A
        return (
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#6b7280' }}>
            N/A
          </Typography>
        );
      },
    },
    {
      id: 'attachments',
      label: 'File đính kèm',
      render: (value: any, item: OrderIncomeExpense) => {
        const documents = documentsMap[item.id] || [];
        
        if (documents.length === 0) {
          return (
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Không có file
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AttachFile sx={{ fontSize: '14px', color: '#6b7280' }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {documents.length} file{documents.length > 1 ? 's' : ''}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center"
        }}>
          <Typography variant="h6">Quản lý thu chi</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateIncomeExpense}
            disabled={!canCreateIncomeExpense}
          >
            Tạo thu chi
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, mx: 2 }} onClose={onClearError}>
          {error}
        </Alert>
      )}


      {/* Main Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {/* Income Expenses List */}
        {(!incomeExpenses || incomeExpenses.length === 0) ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            width: '100%',
            textAlign: "center", 
            py: 4 
          }}>
            <MoneyIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Chưa có giao dịch nào
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tạo giao dịch đầu tiên để bắt đầu quản lý thu chi
            </Typography>
          </Box>
        ) : filteredIncomeExpenses.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: '100%',
            textAlign: "center", 
            py: 4 
          }}>
            <MoneyIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Không tìm thấy giao dịch nào
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Thử thay đổi từ khóa tìm kiếm
            </Typography>
          </Box>
        ) : (
          <DataTableModule
            data={filteredIncomeExpenses}
            columns={columns}
            leftFilterWidth={13}
            getRowId={(item: OrderIncomeExpense) => item.id}
            onRowSelect={() => {}}
            onSelectAll={() => {}}
            onRowClick={handleRowClick}
            isRowSelected={() => false}
            areAllRowsSelected={false}
            areSomeRowsSelected={false}
            showCheckbox={false}
          />
        )}
      </Box>

      {/* Edit Modal */}
      {editLoading && (
        <Box sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
          <CircularProgress />
        </Box>
      )}
      <NewIncomeExpenseModal
        open={isEditModalOpen && !editLoading}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateIncomeExpense}
        editingIncomeExpense={editingIncomeExpense}
        isEditMode={true}
      />


    </Box>
  );
};

export default ListOrderIncomeExpenses;
